import type { RenderableText } from '@rio-cloud/rio-uikit/Bar';
import { ParseError, parsePhoneNumberWithError } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js/max';
import { type IntlShape, type PrimitiveType, useIntl } from 'react-intl';

import type { AdditionalVatSchemaType } from '@/components/VatSchema';

export const addressBuilder = (...addresses: string[]): string => {
    if (addresses.length === 0) {
        return '';
    }

    if (addresses.length === 1) {
        return addresses[0];
    }

    return addresses.join(', ').trim();
};

export type PhoneAndCountry = {
    number: string;
    country: string;
};

export const parseCountryAndPhoneNumber = (phone?: string): PhoneAndCountry | undefined => {
    if (phone) {
        const phoneNumber = parsePhoneNumberWithError(phone);
        if (phoneNumber) {
            return {
                number: phoneNumber.nationalNumber,
                country: phoneNumber.country ?? '',
            };
        }
    }
    return;
};

export const buildPhoneNumber = (countryCode: string, phoneNumber: string) => {
    const phone = parsePhoneNumberWithError(phoneNumber, countryCode as CountryCode);
    if (phone?.isValid()) {
        return phone.number;
    }

    return '';
};

export const phoneNumberValidity = (countryCode: string, phoneNumber: string): boolean => {
    try {
        const parsed = parsePhoneNumberWithError(phoneNumber, {
            defaultCountry: countryCode as CountryCode,
            extract: false,
        });
        if (!parsed) {
            return false;
        }
        if (parsed.country !== (countryCode as CountryCode)) {
            return false;
        }
        return parsed.isValid();
    } catch (error) {
        if (error instanceof ParseError) {
            console.error(error.message);
        } else {
            throw error;
        }
    }
    return false;
};

export const getVatCountries = (
    countryCode: string,
    additional: AdditionalVatSchemaType,
    country: string
): string[] => {
    const result = additional.map(c => {
        if (c.country !== countryCode) {
            return c.country;
        }

        return '';
    });
    return [country, ...result];
};

export const includesAny = <T>(arr: T[] | undefined | null, values: T[] | T | undefined | null): boolean => {
    if (!arr || !values) {
        return false;
    }

    if (!Array.isArray(values)) {
        return arr?.includes(values);
    }

    return values.some(v => arr.includes(v));
};

// Intl helper function - use this when you have to use the intl available
export const translate = (id: string, intl: IntlShape, values?: Record<string, PrimitiveType>) =>
    intl.formatMessage({ id }, values);

// Hook version - use this in React components when you don't have intl
export const t = (id: string, values?: Record<string, PrimitiveType>) => useIntl().formatMessage({ id }, values);

export const stringifyQueryParams = (params: Object) =>
    Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

export const formatEnergyValue = (
    value: number,
    locale: string,
    unit = 'kWh',
    decimalPlaces = 2,
    roundingMode: 'ceil' | 'floor' | 'trunc' | 'halfEven' = 'halfEven'
): { value: string; unit: string } => {
    const formattedValue = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        roundingMode,
    });

    return {
        value: formattedValue.format(value),
        unit,
    };
};

export const formatCurrencyValue = (
    value: number | RenderableText,
    currency: string,
    locale: string,
    decimalPlaces = 2,
    roundingMode: 'ceil' | 'floor' | 'trunc' | 'halfEven' = 'halfEven'
) => {
    const formattedValue = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        roundingMode,
    } as Intl.NumberFormatOptions);

    return {
        value: formattedValue.format(Number(value)),
        unit: currency,
    };
};

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

// Returns a "dd MMM yyyy" or "dd MMM yy" date string with the month abbreviation based on user locale
export const formatDateLocalized = (
    date: Date,
    locale: string,
    showTime = false,
    formatPattern = 'dd MMM yyyy'
): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const yearFull = date.getFullYear().toString();
    const yearShort = yearFull.slice(-2);

    // Get the first 3 characters of the translated month
    const fullMonth = date.toLocaleString(locale, { month: 'long' });
    const monthShort = fullMonth.slice(0, 3);
    const monthFormatted = monthShort.charAt(0).toUpperCase() + monthShort.slice(1).toLowerCase();

    // Replace tokens in the right format
    let formattedDate = formatPattern
        .replace('dd', day)
        .replace('MMMM', fullMonth)
        .replace('MMM', monthFormatted)
        .replace('yyyy', yearFull)
        .replace('yy', yearShort);

    // Add hours and minutes in 24h format
    if (showTime) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        formattedDate += ` | ${hours}:${minutes}`;
    }

    return formattedDate;
};

export const formatUrl = (url: string, language: string) => url.replace('{language}', language);
